import type { BenefitSummary } from "@yutai/shared"
import { Link } from "react-router-dom"
import { CategoryBadge } from "../../components/CategoryBadge.js"
import { HighlightMatch } from "../../components/HighlightMatch.js"
import { googleSearchUrl, yahooFinanceJpUrl } from "../../lib/financeLinks.js"
import { formatDeliveryMonths } from "../../lib/formatMonths.js"
import { MARKET_CAP_TIER_LABELS, marketCapTierFromValue } from "../../lib/marketCapTier.js"
import { RELEVANCE_LEVEL_LABELS, relevanceLevelFromScore } from "../../lib/relevance.js"
import styles from "./BenefitCard.module.css"
import { combinedYield } from "./filterBenefits.js"

interface Props {
  benefit: BenefitSummary
  query?: string
}

export function BenefitCard({ benefit, query = "" }: Props) {
  const monthsLabel = benefit.recordMonths.map((m) => `${m}月`).join("・")
  const deliveryMonthsLabel =
    benefit.deliveryMonths.length > 0 ? formatDeliveryMonths(benefit.deliveryMonths) : null
  const relevanceLevel = relevanceLevelFromScore(benefit.benefitRelevanceScore)
  const combined = combinedYield(benefit)
  const capTier = marketCapTierFromValue(benefit.marketCap)

  return (
    <article className={styles.card}>
      <div className={styles.top}>
        <div className={styles.companyInfo}>
          <div className={styles.companyMeta}>
            <a
              href={yahooFinanceJpUrl(benefit.id)}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.codeLink}
              title="Yahoo!ファイナンスで株価を見る"
            >
              {benefit.id}
            </a>
            <span className={styles.industry}>| {benefit.industry}</span>
            {capTier && (
              <span className={styles.industry}>| {MARKET_CAP_TIER_LABELS[capTier]}</span>
            )}
          </div>
          <h2 className={styles.name}>
            <Link to={`/companies/${benefit.id}`}>{benefit.name}</Link>
          </h2>
          {benefit.businessDescription && (
            <p className={styles.businessDescription}>
              <HighlightMatch text={benefit.businessDescription} query={query} />
            </p>
          )}
        </div>
      </div>

      {benefit.categories.length > 0 && (
        <div className={styles.badges}>
          {benefit.categories.map((cat) => (
            <CategoryBadge key={cat} category={cat} />
          ))}
        </div>
      )}

      <div className={styles.benefitSection}>
        {benefit.description && (
          <p className={styles.description}>
            <HighlightMatch text={benefit.description} query={query} />
          </p>
        )}

        {benefit.tiers.length > 0 && (
          <ul className={styles.tiers}>
            {benefit.tiers.map((tier, i) => (
              <li key={`${tier.minimumShares}-${i}`} className={styles.tier}>
                <span className={styles.tierShares}>
                  {tier.minimumShares != null
                    ? `${tier.minimumShares.toLocaleString()}株以上`
                    : "株数不明"}
                </span>
                <span>
                  <HighlightMatch text={tier.description} query={query} />
                </span>
              </li>
            ))}
          </ul>
        )}

        <div className={styles.statGrid}>
          <div className={styles.stat}>
            <span className={styles.statLabel}>権利確定</span>
            <span className={styles.statValue}>{monthsLabel}</span>
          </div>
          {deliveryMonthsLabel && (
            <div className={styles.stat}>
              <span className={styles.statLabel}>送付月</span>
              <span className={styles.statValue}>{deliveryMonthsLabel}</span>
            </div>
          )}
        </div>

        {(benefit.dividendYield != null || benefit.benefitYield != null || combined != null) && (
          <div className={styles.statGrid}>
            {benefit.dividendYield != null && (
              <div className={styles.stat}>
                <span className={styles.statLabel}>配当利回り</span>
                <span className={styles.statValue}>{benefit.dividendYield.toFixed(1)}%</span>
              </div>
            )}
            {benefit.benefitYield != null && (
              <div className={styles.stat}>
                <span className={styles.statLabel}>優待利回り</span>
                <span className={styles.statValue}>{benefit.benefitYield.toFixed(1)}%</span>
              </div>
            )}
            {combined != null && (
              <div
                className={styles.stat}
                title="最低必要株数を保有した場合の、事業年度末時点の株価水準に基づく概算値"
              >
                <span className={styles.statLabel}>総合利回り</span>
                <span className={`${styles.statValue} ${styles.statValueAccent}`}>
                  {combined.toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        )}

        {(benefit.holdingYears.length > 0 || relevanceLevel) && (
          <div className={styles.noteRow}>
            {benefit.holdingYears.length > 0 && (
              <span className={styles.longTermNote}>長期保有特典あり</span>
            )}
            {relevanceLevel && (
              <span className={styles.relevanceNote}>
                事業関連度: {RELEVANCE_LEVEL_LABELS[relevanceLevel]}
              </span>
            )}
          </div>
        )}

        <div className={styles.links}>
          <a
            href={benefit.officialUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            公式優待ページ ↗
          </a>
          <a
            href={yahooFinanceJpUrl(benefit.id)}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            Yahoo!ファイナンス ↗
          </a>
          <a
            href={googleSearchUrl(benefit.name)}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            優待ブログ検索 ↗
          </a>
        </div>
      </div>
    </article>
  )
}
